import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * One-tap add for the cart drawer's "Add to your order" rail.
 *
 * Speaks the theme's own cart event protocol: dispatch CartLinesUpdateEvent
 * (action 'add') with a deferred promise BEFORE the network call, then
 * resolve it with the fresh cart and the re-rendered section HTML. The
 * drawer's cart-items-component consumes the promise to morph its contents,
 * which re-renders the tier progress bar and this rail in the same pass.
 * The add flow mirrors product-form.js so behaviour stays identical.
 */
class KdCartRecs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onDocClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onDocClick);
  }

  /** Close any open size row when tapping outside the rail. */
  #onDocClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    for (const el of this.querySelectorAll('.kd-rec__sizes:not([hidden])')) {
      el.hidden = true;
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const addBtn = target.closest('[data-kd-rec-add]');
    if (addBtn) {
      event.preventDefault();
      const tile = addBtn.closest('[data-kd-rec]');
      const sizes = tile?.querySelector('.kd-rec__sizes');
      if (!sizes) return;
      const wasHidden = sizes.hidden;
      for (const el of this.querySelectorAll('.kd-rec__sizes:not([hidden])')) {
        el.hidden = true;
      }
      sizes.hidden = !wasHidden;
      return;
    }

    const sizeBtn = target.closest('[data-kd-variant]');
    if (sizeBtn) {
      event.preventDefault();
      const tile = sizeBtn.closest('[data-kd-rec]');
      this.#add(sizeBtn.getAttribute('data-kd-variant'), tile);
    }
  };

  /**
   * @param {string | null} variantId
   * @param {Element | null | undefined} tile
   */
  async #add(variantId, tile) {
    if (!variantId) return;
    tile?.classList.add('is-busy');

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-recs',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      }).then((r) => r.json());

      if (response.status) {
        throw new Error(response.message || 'Add to cart failed');
      }

      const ajaxCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'kd-cart-recs',
          sourceId: 'kd-cart-recs',
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      tile?.classList.remove('is-busy');
      console.warn('[kd-cart-recs] add failed:', error);
    }
  }
}

if (!customElements.get('kd-cart-recs')) {
  customElements.define('kd-cart-recs', KdCartRecs);
}
